import React from "react";
import { useState, useRef, useEffect } from "react";
import { ErrorMessage } from "@hookform/error-message";
import "./InputForm.scss";

const Input = ({
  label,
  name,
  register,
  type,
  errors,
  onBlur = () => {},
  onChange = () => {},
  disabled,
  value,
  dirtyFields,
  className,
  labelClassName = "p-title",
  toggleVisibility,
  showPassword,
  isPasswordField = false,
}) => {
  const handleKeyPress = (e) => {
    if (type === "tel" && !/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <span>
      <label className={labelClassName} htmlFor={name}>
        {label}
      </label>
      <div className="input-wrapper" style={{ position: "relative" }}>
        <input
          className={`${className} ${dirtyFields && dirtyFields[name] ? "dirtyFields" : ""}`}
          type={type}
          {...register(name, {
            onBlur: (e) => {
              onBlur(e);
            },
            onChange: (e) => {
              onChange(e);
            },
          })}
          id={name}
          disabled={disabled}
          value={value}
          onKeyPress={handleKeyPress}
          style={isPasswordField ? { paddingRight: "40px" } : {}}
        />
        {isPasswordField && (
          <span
            onClick={toggleVisibility}
            className="password-icon"
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showPassword ? (
              <i className="fa fa-eye-slash"></i>
            ) : (
              <i className="fa fa-eye"></i>
            )}
          </span>
        )}
      </div>
      {errors ? (
        <div style={{ color: "red" }}>
          {errors[name] ? errors[name].message : null}
        </div>
      ) : null}
    </span>
  );
};

export default Input;

export const Select = React.forwardRef(
  ({ name, label, options = [], errors, onChange, className, value }, ref) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedLabel, setSelectedLabel] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);
    const nativeRef = useRef(null);

    const setRefs = (el) => {
      nativeRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    };

    useEffect(() => {
      if (!value || !options.length) {
        if (!value) {
          setSelectedLabel("");
          setSelectedOption(null);
        }
        return;
      }
      const found = options.find(
        (o) => String(o.slug || o.Id || o.id) === String(value),
      );
      if (found) {
        setSelectedLabel(found.Name || found.name);
        setSelectedOption(found);
      }
    }, [value, options]);

    useEffect(() => {
      const handler = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target))
          setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
      if (open) searchRef.current?.focus();
    }, [open]);

    const filtered = options.filter((o) =>
      (o.Name || o.name || "").toLowerCase().includes(search.toLowerCase()),
    );

    const triggerNativeChange = (val) => {
      const el = nativeRef.current;
      if (!el) return;
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        "value",
      )?.set;
      if (nativeSetter) nativeSetter.call(el, val);
      else el.value = val;
      el.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const handleSelect = (option) => {
      const val = option.slug || option.Id || option.id;
      const lbl = option.Name || option.name;
      setSelectedLabel(lbl);
      setSelectedOption(option);
      setOpen(false);
      setSearch("");
      triggerNativeChange(val);
      if (onChange) onChange({ target: { id: name, name, value: val } });
    };

    const handleClear = (e) => {
      e.stopPropagation();
      setSelectedLabel("");
      setSelectedOption(null);
      setSearch("");
      triggerNativeChange("");
      if (onChange) onChange({ target: { id: name, name, value: "" } });
    };

    const isCategory = !!selectedOption?.slug;
    const showClear = selectedLabel && !isCategory;

    return (
      <div className={`ss-field ${className || ""}`} ref={wrapperRef}>
        {label && <label className="ss-label">{label}</label>}

        <select
          ref={setRefs}
          name={name}
          id={name}
          style={{ display: "none" }}
          defaultValue=""
        >
          <option value="" />
          {options.map((o) => {
            const val = o.slug || o.Id || o.id;
            return (
              <option key={val} value={val}>
                {o.Name || o.name}
              </option>
            );
          })}
        </select>

        <button
          type="button"
          className={`ss-trigger ${open ? "ss-trigger--open" : ""} ${selectedLabel ? "ss-trigger--selected" : ""}`}
          onClick={() => setOpen((p) => !p)}
        >
          <span className="ss-trigger-text">{selectedLabel || "בחר"}</span>
          <span className="ss-trigger-icons">
            {showClear && (
              <span className="ss-clear" onClick={handleClear} title="נקה">
                ✕
              </span>
            )}
            <span className="ss-arrow">{open ? "▲" : "▼"}</span>
          </span>
        </button>

        {open && (
          <div className="ss-dropdown">
            <div className="ss-search-row">
              <input
                ref={searchRef}
                type="text"
                className="ss-search"
                placeholder={`חיפוש${label ? " " + label : ""}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="ss-list">
              {filtered.length === 0 ? (
                <div className="ss-empty">לא נמצאו תוצאות</div>
              ) : (
                filtered.map((o) => {
                  const val = o.slug || o.Id || o.id;
                  const lbl = o.Name || o.name;
                  const isSelected = lbl === selectedLabel;
                  return (
                    <div
                      key={val}
                      className={`ss-option ${isSelected ? "ss-option--selected" : ""}`}
                      onClick={() => handleSelect(o)}
                    >
                      {isSelected && <span className="ss-option-check">✓</span>}
                      {lbl}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {errors?.[name] && (
          <span className="ss-error">{errors[name]?.message}</span>
        )}
      </div>
    );
  },
);
